import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext.jsx'
import { getUnfiledPaidPatents } from '../utils/filingConfirmation.js'

/**
 * App-wide "welcome back, confirm your filing" reminder. Shown as a compact top
 * bar for a paid patent that's finished generating but isn't confirmed filed.
 *
 * Deliberately NOT a within-session nag: it never appears while the user is in
 * the create/file flow, and it only shows patents that were already awaiting
 * confirmation when this session started (snapshot at login) — so a patent you
 * pay for and generate this session won't nag you until you leave and come back.
 * "Open patent" routes to the record to confirm; ✕ dismisses for the session.
 */
export default function FilingReminderModal() {
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const [patents, setPatents] = useState([])
  const [loaded, setLoaded] = useState(false)
  // In-memory only: the banner unmounts on the login page, so dismissal naturally
  // resets on the next login (and on a reload) — no sessionStorage to go stale.
  const [dismissed, setDismissed] = useState(false)

  // Snapshot of patent ids eligible to nag THIS session, captured once at
  // login/page-load. Only patents already awaiting confirmation when the session
  // started may show — patents created/generated later this session don't (until
  // the next return). Re-checks still let the bar CLEAR filed ones; they never
  // ADD new ones.
  const eligibleIds = useRef(null)
  const snapshotUid = useRef(null)

  // Never nag while the user is inside the create/file flow.
  const onPatentFlow = location.pathname.startsWith('/new-patent')

  useEffect(() => {
    let active = true
    if (!user?.uid) {
      setPatents([])
      setLoaded(false)
      eligibleIds.current = null
      snapshotUid.current = null
      return
    }
    getUnfiledPaidPatents(user.uid)
      .then((list) => {
        if (!active) return
        // First result after a (re)login → capture this session's eligible set.
        if (eligibleIds.current === null || snapshotUid.current !== user.uid) {
          eligibleIds.current = new Set(list.map((p) => p.patentId))
          snapshotUid.current = user.uid
        }
        setPatents(list.filter((p) => eligibleIds.current.has(p.patentId)))
        setLoaded(true)
      })
      .catch((err) => {
        console.error('[filing] reminder check failed:', err)
        if (active) setLoaded(true)
      })
    return () => {
      active = false
    }
  }, [user?.uid, location.pathname])

  if (!user?.uid || dismissed || onPatentFlow || !loaded || patents.length === 0) return null

  const current = patents[0]
  const title = current.title || current.inventionTitle || 'your patent'
  const extra = patents.length - 1

  const handleDismiss = () => setDismissed(true)

  // Note: does NOT dismiss — the bar should persist on every tab until ✕.
  const handleOpenPatent = () => navigate(`/edit-patent/${current.patentId}`)

  return (
    <div className="filing-reminder-banner" style={styles.banner} role="alert">
      <span style={styles.text}>
        Have you filed <strong>"{title}"</strong> with the USPTO? Confirm your filing to keep it on record.
        {extra > 0 && <span style={styles.count}> (+{extra} more)</span>}
      </span>

      <div style={styles.actions}>
        <button style={styles.openBtn} onClick={handleOpenPatent}>
          Open patent
        </button>
        <button style={styles.close} onClick={handleDismiss} aria-label="Dismiss reminder">
          ×
        </button>
      </div>
    </div>
  )
}

const FONT = "'Red Hat Display', 'Inter', sans-serif"

const styles = {
  banner: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '14px',
    padding: '12px 20px',
    background: '#FFF5F2',
    borderBottom: '1px solid rgba(239, 105, 57, 0.28)',
    color: '#1A1A1A',
    fontFamily: FONT,
    fontSize: '15px',
    lineHeight: 1.4,
    textAlign: 'center',
  },
  text: { fontWeight: 500, fontFamily: FONT },
  count: { opacity: 0.6 },
  actions: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  openBtn: {
    border: '1.5px solid #EF6939',
    borderRadius: '999px',
    background: '#fff',
    color: '#EF6939',
    fontWeight: 600,
    fontFamily: FONT,
    fontSize: '14px',
    padding: '8px 18px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  close: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    border: 'none',
    background: 'transparent',
    fontSize: '20px',
    lineHeight: 1,
    color: '#9A8F88',
    cursor: 'pointer',
    borderRadius: '999px',
  },
}
